import { BUSINESS_TZ_OFFSET_MS, businessDayStartUtcMs } from '@nadoutong/contracts';
import type { OpsUsageWindow } from '../../api/opsUsage.ts';

/**
 * Window pills on the Customer 360 usage tab. `today` / `7d` / `30d` /
 * `custom` are ops-api kinds; `week` and `month` are calendar windows
 * sent as `window=custom` with UTC+8 bounds (the daily endpoint has no
 * week/month kind).
 */
export type UsageTabWindow = 'today' | 'week' | 'month' | '7d' | '30d' | 'custom';

export const USAGE_TAB_WINDOWS: readonly UsageTabWindow[] = [
  'today',
  'week',
  'month',
  '7d',
  '30d',
  'custom',
];

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export interface UsageTabQuery {
  readonly window: OpsUsageWindow;
  readonly from?: string;
  readonly to?: string;
  /** Calendar windows (today / this week / this month) show extrapolation. */
  readonly calendar: boolean;
  /** `today` is hourly; every other pill is daily. Sent explicitly — no server default. */
  readonly granularity: 'day' | 'hour';
}

interface Utc8Parts {
  readonly y: number;
  readonly m: number;
  readonly d: number;
  readonly weekday: number;
}

function utc8Parts(ms: number): Utc8Parts {
  const shifted = new Date(ms + BUSINESS_TZ_OFFSET_MS);
  return {
    y: shifted.getUTCFullYear(),
    m: shifted.getUTCMonth(),
    d: shifted.getUTCDate(),
    weekday: shifted.getUTCDay(),
  };
}

/** `YYYY-MM-DD` of the UTC+8 business day containing `ms`. */
export function utc8DateString(ms: number): string {
  const { y, m, d } = utc8Parts(ms);
  return `${String(y)}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Gregorian weekday of a `YYYY-MM-DD` business date (0 = Sunday).
 * The label is a calendar date, not a UTC instant — parse as UTC Y-M-D
 * so 16:00Z the previous day (UTC+8 midnight) cannot shift the weekday.
 */
export function weekdayOfBusinessDate(date: string): number {
  const ymd = date.slice(0, 10);
  const parts = ymd.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return 0;
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** `YYYY-MM-DD HH24` of the UTC+8 hour containing `ms`. */
export function utc8HourKey(ms: number): string {
  const shifted = new Date(ms + BUSINESS_TZ_OFFSET_MS);
  return `${utc8DateString(ms)} ${String(shifted.getUTCHours()).padStart(2, '0')}`;
}

/** Fraction of the current UTC+8 hour that has elapsed, 0..1. */
export function utc8HourFraction(ms: number): number {
  const start = Math.floor(ms / HOUR_MS) * HOUR_MS;
  return Math.min(1, Math.max(0, (ms - start) / HOUR_MS));
}

export function isCalendarWindow(window: UsageTabWindow): boolean {
  return window === 'today' || window === 'week' || window === 'month';
}

function startOfUtc8Week(nowMs: number): number {
  const dayStart = businessDayStartUtcMs(nowMs);
  const daysFromMonday = (utc8Parts(dayStart).weekday + 6) % 7;
  return dayStart - daysFromMonday * DAY_MS;
}

function startOfUtc8Month(nowMs: number): number {
  const { y, m } = utc8Parts(nowMs);
  return Date.UTC(y, m, 1, 0, 0, 0, 0) - BUSINESS_TZ_OFFSET_MS;
}

function startOfNextUtc8Month(nowMs: number): number {
  const { y, m } = utc8Parts(nowMs);
  return Date.UTC(y, m + 1, 1, 0, 0, 0, 0) - BUSINESS_TZ_OFFSET_MS;
}

export function resolveUsageTabQuery(
  pill: UsageTabWindow,
  customFrom: string,
  customTo: string,
  nowMs: number,
): UsageTabQuery {
  if (pill === 'today') return { window: 'today', calendar: true, granularity: 'hour' };
  if (pill === '7d') return { window: '7d', calendar: false, granularity: 'day' };
  if (pill === '30d') return { window: '30d', calendar: false, granularity: 'day' };
  if (pill === 'week') {
    const start = startOfUtc8Week(nowMs);
    return {
      window: 'custom',
      from: new Date(start).toISOString(),
      to: new Date(start + 7 * DAY_MS).toISOString(),
      calendar: true,
      granularity: 'day',
    };
  }
  if (pill === 'month') {
    return {
      window: 'custom',
      from: new Date(startOfUtc8Month(nowMs)).toISOString(),
      to: new Date(startOfNextUtc8Month(nowMs)).toISOString(),
      calendar: true,
      granularity: 'day',
    };
  }
  return {
    window: 'custom',
    from: customFrom.length > 0 ? customFrom : undefined,
    to: customTo.length > 0 ? customTo : undefined,
    calendar: false,
    granularity: 'day',
  };
}

/** Fraction of the current UTC+8 business day that has elapsed, 0..1. */
export function utc8DayFraction(nowMs: number): number {
  const start = businessDayStartUtcMs(nowMs);
  return Math.min(1, Math.max(0, (nowMs - start) / DAY_MS));
}

export function occurredThroughIndex(
  dates: readonly string[],
  today: string,
): number {
  let last = -1;
  dates.forEach((date, index) => {
    if (date <= today) last = index;
  });
  return last;
}

/**
 * How many days of the current window have actually elapsed, for the
 * daily-average spend KPI. Calendar pills use the in-progress bucket
 * fraction; rolling 7d/30d/custom use wall-clock `(min(to, now) - from)`
 * so zero-filled extra dates (a 7d window spanning 8 UTC+8 midnights)
 * cannot inflate the denominator.
 */
export function elapsedWindowDays(
  pill: UsageTabWindow,
  window: { readonly from: string; readonly to: string },
  nowMs: number,
  occurredIndex: number,
  dayFraction: number,
): number {
  if (pill === 'today') return Math.max(dayFraction, 1e-9);
  if (pill === 'week' || pill === 'month') {
    return Math.max(occurredIndex + dayFraction, 1e-9);
  }
  const fromMs = Date.parse(window.from);
  const toMs = Date.parse(window.to);
  const startMs = Number.isFinite(fromMs) ? fromMs : nowMs;
  const endMs = Number.isFinite(toMs) ? Math.min(toMs, nowMs) : nowMs;
  return Math.max((endMs - startMs) / DAY_MS, 1e-9);
}

/**
 * Fractional x-coordinate of "now" on a zero-filled series, or
 * `undefined` when the whole window is in the past or the future —
 * those ranges must not grow a now-marker or a not-yet shade.
 */
export function nowAtCoordinate(
  dates: readonly string[],
  occurredIndex: number,
  nowFraction: number,
  nowBucket: string,
): number | undefined {
  const last = dates.length - 1;
  if (occurredIndex < 0 || last < 0) return undefined;
  const lastDate = dates[last];
  if (lastDate !== undefined && occurredIndex === last && lastDate < nowBucket) {
    return undefined;
  }
  return occurredIndex + nowFraction;
}

/**
 * Default table order: newest-first for day grain. Hour grain keeps
 * the in-progress bucket (and the hours before it) at the top so the
 * default 9 rows are not the still-future midnight hours.
 */
export function orderUsageDetailRows<T>(
  rows: readonly T[],
  occurredIndex: number,
  hourly: boolean,
): T[] {
  if (!hourly) return [...rows].reverse();
  if (occurredIndex < 0) return [...rows];
  const occurred = rows.slice(0, occurredIndex + 1);
  const future = rows.slice(occurredIndex + 1);
  return [...occurred].reverse().concat(future);
}
